import { Button } from 'module/ui/components/atoms/button';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { InputStyle } from 'module/ui/components/atoms/input/input';
import { Popover } from 'module/ui/components/atoms/popover';
import { Select, SelectProps } from 'module/ui/components/atoms/select';
import { Label01, SingleLineEllipsis } from 'module/ui/components/styles';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Control } from './control';
import { Option } from './option';
import { VirtualizedMenuList } from './virtualized-menu-list';

const Title = styled.div`
  padding: var(--spacing-01) var(--spacing-05) var(--spacing-01)
    var(--spacing-03);
  ${Label01};
  color: var(--text-tertiary);
  height: 24px;
`;
const TriggerContent = styled.div``;
const Divider = styled.div`
  width: 1px;
  background-color: var(--field-border-default);
`;
const Trigger = styled.div.attrs({
  className: 'custom-input',
})`
  display: flex;
  align-items: center;
  justify-content: space-between;
  ${InputStyle};
  gap: var(--spacing-03);
  ${TriggerContent} {
    flex: 1;
    ${SingleLineEllipsis};
  }
  ${Divider} {
    height: 16px;
    margin: 0 var(--spacing-03);
  }
  > ${FontAwesomeIcon} {
    color: var(--icon-secondary);
  }
`;
const StyledSelect = styled(Select)`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  .ozone-select__menu {
    border: none;
    box-shadow: none;
    position: initial;
    margin: 0;
    flex: 1 1 100%;
    overflow-y: auto;
  }
`;
const Footer = styled.div`
  border-top: 1px solid var(--divider-subtle);
  padding: var(--spacing-03);
  ${Button} {
    width: 100%;
    justify-content: center;
  }
`;
const PopoverContent = styled.div<{ width: number }>`
  min-width: 304px;
  width: ${({ width }) => width}px;
  padding: calc(var(--spacing-01) + var(--spacing-02))
    calc(var(--spacing-01) + var(--spacing-01)) var(--spacing-01);
`;

export interface PopoverSelectProps extends SelectProps {
  title: ReactNode;
  triggerContent: ReactNode;
  /** Passed to default VirtualizedMenuList for infinite scroll */
  onLoadMore?: () => void;
  hasMore?: boolean;
}
export const PopoverSelect = ({
  title,
  triggerContent,
  components,
  onChange,
  value,
  isLoading,
  onLoadMore,
  hasMore,
  ...restProps
}: PopoverSelectProps) => {
  const [visible, setVisible] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [internalValue, setInternalValue] = useState(value ?? []);
  const resetInternalValue = useCallback(() => {
    setInternalValue(value ?? []);
  }, [value]);
  const onOpenChange = useCallback(
    (open: boolean) => {
      setVisible(open);
      if (!open) {
        onChange?.(internalValue);
      }
      resetInternalValue();
    },
    [internalValue, onChange, resetInternalValue],
  );
  const onDone = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  useEffect(() => {
    resetInternalValue();
  }, [resetInternalValue]);

  return (
    <Popover
      trigger={['click']}
      placement="bottomLeft"
      open={visible}
      onOpenChange={onOpenChange}
      content={
        <PopoverContent width={triggerRef.current?.offsetWidth ?? 240}>
          <Title>{title}</Title>
          <StyledSelect
            {...restProps}
            isLoading={isLoading}
            onLoadMore={onLoadMore}
            hasMore={hasMore}
            menuIsOpen
            hideSelectedOptions={false}
            controlShouldRenderValue={false}
            value={internalValue}
            onChange={setInternalValue}
            components={{
              Control: Control,
              Option: Option,
              DropdownIndicator: null,
              MenuList: VirtualizedMenuList,
              ClearIndicator: null,
              ...components,
            }}
          />
          <Footer>
            <Button type="primary" onClick={onDone}>
              <FormattedMessage id="done" />
            </Button>
          </Footer>
        </PopoverContent>
      }
    >
      <Trigger onClick={() => setVisible(!visible)} ref={triggerRef}>
        <TriggerContent>{triggerContent}</TriggerContent>
        <Divider />
        <FontAwesomeIcon icon={faChevronDown} size={12} />
      </Trigger>
    </Popover>
  );
};
